import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import moment from 'moment';
import { orderBy } from 'lodash';
import { useDateRange } from '../../util/useDateRange';
import DepositsService from '../../services/DepositsService';
import {
  getDepositStatus,
  getDepositsTableConfig,
  DEPOSIT_STATUS,
} from './config';
import SearchForm from './SearchForm';
import DataTable from './DataTable';
import LoadingOverlay from '../LoadingOverlay';
import ErrorMessage from '../ErrorMessage';
import DepositRefundModal from './DepositRefundModal';

function useDepositsFilters({ fetching }) {
  const [startDate, endDate, datePicker] = useDateRange({
    useFullDatePicker: true,
  });
  const [customerName, setCustomerName] = useState();
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const renderFilters = () => (
    <>
      {datePicker}
      <div className="results-deals-header">
        <div>
          <div className="data-table-label">Search By</div>
          <SearchForm
            placeholder="Customer Name"
            onSubmit={setCustomerName}
            disabled={fetching}
          />
        </div>
        <div>
          <div className="data-table-label">Search By Status</div>
          <Typeahead
            id="deposits-status-typeahead"
            placeholder="Status"
            disabled={fetching}
            className="deposits-status-typeahead"
            labelKey="name"
            multiple
            selectHintOnEnter
            clearButton
            options={[
              { name: 'Active', value: DEPOSIT_STATUS.ACTIVE },
              { name: 'Vehicle Sold', value: DEPOSIT_STATUS.VEHICLE_SOLD },
              { name: 'Refunded', value: DEPOSIT_STATUS.REFUNDED },
            ]}
            selected={selectedStatuses}
            onChange={selected => setSelectedStatuses(selected)}
          />
        </div>
      </div>
    </>
  );
  return {
    startDate,
    endDate,
    customerName,
    selectedStatuses,
    renderFilters,
  };
}

export function Deposits() {
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const {
    startDate,
    endDate,
    customerName,
    selectedStatuses,
    renderFilters,
  } = useDepositsFilters({ fetching });

  const [deposits, setDeposits] = useState([]);
  const fetchCountRef = useRef(0);
  /**
   * Refreshes the deposits list based on filters
   */
  const refreshDeposits = () => {
    setFetching(true);
    setError(null);
    setDeposits([]);
    // eslint-disable-next-line no-plusplus
    const fetchId = ++fetchCountRef.current;

    const effectiveStartDate = moment(startDate);
    effectiveStartDate.startOf('day');
    const effectiveEndDate = moment(endDate);
    effectiveEndDate.endOf('day');
    DepositsService.getAllDeposits(
      effectiveStartDate.toISOString(),
      effectiveEndDate.toISOString(),
      customerName,
    )
      .then(newDeposits => {
        if (fetchId === fetchCountRef.current) {
          setDeposits(orderBy(newDeposits, 'createdAt', 'desc'));
          setFetching(false);
        }
      })
      .catch(err => {
        setError(err);
        setFetching(false);
      });
  };
  // fetch deposits when filters change
  useEffect(refreshDeposits, [
    startDate.toISOString(),
    endDate.toISOString(),
    customerName,
  ]);

  /**
   * Handles refund requests for deposits
   */
  const onRefundRequested = depositId => {
    DepositRefundModal().then(confirmChange => {
      if (!confirmChange) return;

      setFetching(true);
      setError(null);
      DepositsService.refundDeposit(depositId)
        .then(refreshDeposits)
        .catch(err => {
          setError(err);
          setFetching(false);
        });
    });
  };

  // filter deposits based on status
  const filteredDeposits = useMemo(
    () =>
      deposits.filter(
        deposit =>
          !selectedStatuses.length ||
          selectedStatuses.find(s => s.value === getDepositStatus(deposit)),
      ),
    [deposits, selectedStatuses],
  );

  const renderIntro = () => (
    <div className="deposit-dashboard-intro">
      The Deposits dashboard helps you organize and search for all Active,
      Refunded, and Completed deals currently in your system.
      <div className="deposits-dashboard-legend">
        <span className="active-deposit">Active</span> = Deposit has been
        successfully paid but sale has not gone through <br />
        <span className="vehicle-sold-deposit">Vehicle Sold</span> = Sale of
        vehicle completed <br />
        <span className="refunded-deposit">Refunded</span> = Successfully
        refunded the customer{"'"}s money back to their credit card <br />
      </div>
    </div>
  );
  return (
    <div className="deposits-dashboard">
      {renderIntro()}
      {error && <ErrorMessage error={error} />}
      <LoadingOverlay active={fetching}>
        {renderFilters()}
        <DataTable
          data={filteredDeposits}
          config={getDepositsTableConfig(onRefundRequested)}
        />
      </LoadingOverlay>
    </div>
  );
}
